import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useFormularioDinamico } from '../../../../hooks/useFormularioDinamico';

function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

export default function FormatoControlPlagas({ usuario, onBack }) {
  // Conectamos con el motor lógico y de ABAC
  const {
    formulario, respuestas: datosServer, loading, enviando, sedes,
    handleChange: handleServerChange, handleSubmit
  } = useFormularioDinamico('control-plagas', usuario);

  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });
  const [empleadosDB, setEmpleadosDB] = useState([]);
  const [cargandoEmpleados, setCargandoEmpleados] = useState(false);
  const [buscarTodasSedes, setBuscarTodasSedes] = useState(false);

  // Cargar lista de empleados para el autocompletado del verificador
  useEffect(() => {
    const cargarEmpleados = async () => {
      setCargandoEmpleados(true);
      try {
        const token = localStorage.getItem("token");
        const API_URL = process.env.REACT_APP_API_URL;
        const resEmp = await fetch(`${API_URL}/api/empleados`, {
          headers: { ...(token && { Authorization: `Bearer ${token}` }) }
        });
        const dataEmp = await resEmp.json();
        setEmpleadosDB(Array.isArray(dataEmp) ? dataEmp : (dataEmp.data || []));
      } catch (error) {
        console.error("Error cargando empleados:", error);
      } finally {
        setCargandoEmpleados(false);
      }
    };
    cargarEmpleados();
  }, []);

  // Valores por defecto iniciales
  useEffect(() => {
    if (!datosServer.fechaEvento) handleServerChange('fechaEvento', new Date().toISOString().split('T')[0]);
    if (!datosServer.turno) handleServerChange('turno', 'Mañana');
    if (!datosServer.tipoPlaga) handleServerChange('tipoPlaga', 'NINGUNA');
    if (!datosServer.ubicacion) handleServerChange('ubicacion', 'NO APLICA');
    if (!datosServer.evidencia) handleServerChange('evidencia', 'NO APLICA');
  }, [datosServer, handleServerChange]);

  // Extraer opciones parametrizadas desde la BD
  const getOpcionesCampo = (nombreCampo) => {
    const campo = formulario?.campos?.find(c => c.nombre_campo === nombreCampo);
    return Array.isArray(campo?.opciones) ? campo.opciones : [];
  };

  const turnosOpts = getOpcionesCampo('turno');
  const tiposPlagaOpts = getOpcionesCampo('tipoPlaga');
  const ubicacionesOpts = getOpcionesCampo('ubicacion');
  const evidenciasOpts = getOpcionesCampo('evidencia');

  const listaParaMostrar = useMemo(() => {
    return empleadosDB.filter(emp => {
      const estaActivo = !emp.empl_fecret || String(emp.empl_fecret).toUpperCase() === 'NULL' || emp.empl_fecret === '0000-00-00';
      if (!estaActivo) return false;
      if (buscarTodasSedes) return true;
      if (!emp.area_nombre || !datosServer.pdv) return false;
      return emp.area_nombre.toUpperCase() === datosServer.pdv.toUpperCase();
    });
  }, [empleadosDB, datosServer.pdv, buscarTodasSedes]);

  const handleCustomChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'pdv') {
      handleServerChange('pdv', value);
      handleServerChange('verificador', '');
    } else if (name === 'tipoPlaga') {
      handleServerChange('tipoPlaga', value);
      if (value === "NINGUNA") {
        handleServerChange('ubicacion', 'NO APLICA');
        handleServerChange('evidencia', 'NO APLICA');
        handleServerChange('accionCorrectiva', '');
      } else {
        if (datosServer.ubicacion === "NO APLICA") handleServerChange('ubicacion', '');
        if (datosServer.evidencia === "NO APLICA") handleServerChange('evidencia', '');
      }
    } else {
      handleServerChange(name, value);
    }
  };

  const guardarLocal = (e) => {
    if (!datosServer.pdv || !datosServer.verificador || !datosServer.tipoPlaga) {
      return setCustomAlert({ show: true, message: "La Sede, el Verificador y el Tipo de Plaga son obligatorios.", type: "warning" });
    }
    
    const hayPlagaLocal = datosServer.tipoPlaga !== "NINGUNA";
    if (hayPlagaLocal && (!datosServer.ubicacion || !datosServer.evidencia || !datosServer.accionCorrectiva)) {
      return setCustomAlert({ show: true, message: "Si se detectó una plaga, debes indicar la Ubicación, la Evidencia y la Acción Correctiva.", type: "warning" });
    }

    handleSubmit(e, () => {
      setCustomAlert({ show: true, message: "Reporte de Control de Plagas guardado exitosamente.", type: "success" });
      setTimeout(() => {
        handleServerChange('tipoPlaga', 'NINGUNA');
        handleServerChange('ubicacion', 'NO APLICA');
        handleServerChange('evidencia', 'NO APLICA');
        handleServerChange('accionCorrectiva', '');
        setCustomAlert(prev => ({ ...prev, show: false }));
      }, 1200);
    });
  };

  if (loading || !formulario) return <div className="p-10 text-center text-white font-bold">Cargando Control de Plagas...</div>;

  const hayPlaga = datosServer.tipoPlaga !== "NINGUNA";

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-4xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      
      {/* CABECERA MORADA */}
      <div className="bg-gradient-to-r from-violet-600/90 to-fuchsia-800/90 border-b border-white/20 px-6 py-4 z-40 flex items-center shadow-md shrink-0">
        <button onClick={onBack} className="mr-4 w-10 h-10 flex-none bg-white/20 hover:bg-white/40 text-white flex items-center justify-center rounded-full transition-all border border-white/40 shadow-sm cursor-pointer">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        <div>
          <h1 className="text-lg sm:text-xl font-black text-white leading-tight">{formulario.titulo}</h1>
          <span className="bg-white/20 text-white border border-white/30 text-[10px] font-bold px-2 py-0.5 rounded-md mt-1 inline-block">CÓDIGO: FT-CD-05 | Versión: {formulario.version}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 custom-scrollbar">
        
        {/* BLOQUE 1: DATOS DEL REPORTE */}
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <h3 className="text-xs font-black text-violet-800 uppercase tracking-widest mb-4 border-b border-violet-200/50 pb-2">Datos Generales</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Fecha *</label>
              <input type="date" name="fechaEvento" value={datosServer.fechaEvento || ""} onChange={handleCustomChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-violet-400 rounded-xl px-3 text-xs font-bold text-slate-800 shadow-sm transition-all m-0" />
            </div>
            
            {/* SEDE DINÁMICA FILTRADA POR ABAC */}
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Sede *</label>
              <select name="pdv" value={datosServer.pdv || ""} onChange={handleCustomChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-violet-400 rounded-xl px-3 text-xs font-bold text-slate-800 shadow-sm cursor-pointer m-0">
                <option value="" disabled>-- Seleccione una sede --</option>
                {sedes.map(sede => (
                  <option key={sede.id} value={sede.nombre.toUpperCase()}>
                    {sede.nombre} {sede.codigo_bodega ? `(Bodega: ${sede.codigo_bodega})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Turno *</label>
              <select name="turno" value={datosServer.turno || ""} onChange={handleCustomChange} className="w-full h-[40px] bg-white/80 border-transparent focus:border-violet-400 rounded-xl px-3 text-xs font-bold text-slate-800 shadow-sm cursor-pointer m-0">
                {turnosOpts.map(turno => <option key={turno} value={turno}>{turno}</option>)}
              </select>
            </div>
          </div>

          <div className="border-t border-slate-200/60 pt-4 relative">
            <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Responsable de la Verificación *</label>
            <input 
              type="text" 
              name="verificador" 
              list="listaVerificadores" 
              value={datosServer.verificador || ""} 
              onChange={handleCustomChange} 
              placeholder={cargandoEmpleados ? "Cargando BD..." : datosServer.pdv ? "Escribe o elige nombre..." : "Elige sede primero"} 
              disabled={!datosServer.pdv || cargandoEmpleados}
              className="w-full h-[40px] bg-white/80 border-transparent focus:border-violet-400 rounded-xl px-3 text-xs font-bold text-slate-800 shadow-sm m-0 disabled:opacity-50" 
              autoComplete="off"
            />
            
            <datalist id="listaVerificadores">
              {listaParaMostrar.map((emp, i) => (
                <option key={i} value={emp.terc_nom}>
                  {buscarTodasSedes ? `${emp.empl_cargo || "Sin Cargo"} (${emp.area_nombre || "Sede Desconocida"})` : (emp.empl_cargo || "Sin Cargo")}
                </option>
              ))}
            </datalist>
            
            <div className="mt-1.5 flex items-center">
              <input 
                type="checkbox" 
                id="buscarGlobalV" 
                checked={buscarTodasSedes} 
                onChange={(e) => setBuscarTodasSedes(e.target.checked)} 
                className="mr-1.5 cursor-pointer accent-violet-600"
                disabled={!datosServer.pdv}
              />
              <label htmlFor="buscarGlobalV" className="text-[9px] text-violet-700 font-black tracking-wide cursor-pointer uppercase">
                Buscar en todas las sedes
              </label>
            </div>
          </div>
        </div>

        {/* BLOQUE 2: INSPECCIÓN Y HALLAZGOS */}
        <div className={`backdrop-blur-xl p-6 rounded-[24px] shadow-sm border transition-colors ${hayPlaga ? 'bg-rose-50/80 border-rose-200' : 'bg-white/60 border-white/80'}`}>
          <h3 className={`text-xs font-black uppercase tracking-widest mb-4 border-b pb-2 ${hayPlaga ? 'text-rose-800 border-rose-200/50' : 'text-violet-800 border-violet-200/50'}`}>
            Hallazgos de Inspección
          </h3>
          
          <div className="mb-5">
            <label className="block text-[10px] font-black text-slate-700 mb-2 uppercase tracking-widest">Tipo de Plaga Identificada *</label>
            <select name="tipoPlaga" value={datosServer.tipoPlaga || ""} onChange={handleCustomChange} className={`w-full h-[45px] font-black rounded-xl px-4 text-xs shadow-sm cursor-pointer border ${hayPlaga ? 'bg-rose-100 text-rose-800 border-rose-300' : 'bg-emerald-100 text-emerald-700 border-emerald-300'} focus:outline-none transition-colors`}>
              {tiposPlagaOpts.map(plaga => <option key={plaga} value={plaga}>{plaga}</option>)}
            </select>
            {hayPlaga && <p className="text-[10px] text-rose-600 font-bold mt-1.5 ml-1 flex items-center gap-1">⚠️ Se ha reportado presencia de plaga. Completa los campos obligatorios a continuación.</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Ubicación / Área o Lugar {hayPlaga && "*"}</label>
              <select 
                name="ubicacion" 
                value={datosServer.ubicacion || ""} 
                onChange={handleCustomChange} 
                disabled={!hayPlaga}
                className="w-full h-[40px] bg-white/90 border border-slate-200 focus:border-violet-400 rounded-xl px-3 text-xs font-bold text-slate-800 shadow-sm disabled:opacity-50 disabled:bg-slate-100 cursor-pointer"
              >
                <option value="" disabled>-- Selecciona --</option>
                {ubicacionesOpts.map(ubi => <option key={ubi} value={ubi}>{ubi}</option>)}
              </select>
            </div>
            
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase truncate h-4">Tipo de Evidencia Encontrada {hayPlaga && "*"}</label>
              <select 
                name="evidencia" 
                value={datosServer.evidencia || ""} 
                onChange={handleCustomChange} 
                disabled={!hayPlaga}
                className="w-full h-[40px] bg-white/90 border border-slate-200 focus:border-violet-400 rounded-xl px-3 text-xs font-bold text-slate-800 shadow-sm disabled:opacity-50 disabled:bg-slate-100 cursor-pointer"
              >
                <option value="" disabled>-- Selecciona --</option>
                {evidenciasOpts.map(evi => <option key={evi} value={evi}>{evi}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-700 mb-2 uppercase tracking-widest">Acciones Correctivas o Preventivas {hayPlaga && "*"}</label>
            <textarea 
              name="accionCorrectiva" 
              value={datosServer.accionCorrectiva || ""} 
              onChange={handleCustomChange} 
              disabled={!hayPlaga}
              placeholder={hayPlaga ? "Describe las medidas tomadas..." : "No requerido si no hay plaga reportada."}
              className="w-full h-[80px] bg-white/90 border border-slate-200 focus:border-violet-400 rounded-xl px-4 py-3 text-xs font-medium text-slate-800 shadow-sm resize-none disabled:opacity-50 disabled:bg-slate-100" 
            />
          </div>

          <button onClick={guardarLocal} disabled={enviando} className={`w-full mt-6 text-white font-black py-4 rounded-xl shadow-lg active:scale-95 transition-all text-[11px] tracking-widest uppercase cursor-pointer border disabled:opacity-50 ${hayPlaga ? 'bg-gradient-to-r from-rose-600 to-red-700 hover:from-rose-500 hover:to-red-600 border-rose-400' : 'bg-gradient-to-r from-violet-600 to-fuchsia-700 hover:from-violet-500 hover:to-fuchsia-600 border-violet-400'}`}>
            {enviando ? 'Guardando...' : 'Guardar Verificación'}
          </button>
        </div>

      </div>

      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : 'Atención'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer ${customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-500 hover:bg-amber-400'}`}>Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}